import type { PoGetSystemStatusResponse } from '@repo/sdk/gen'

// Data older than this (both daily sync rounds missed) is stale: mai fingere
// freschezza. Read by the dashboard, the app shell's banner and the
// new-document dialog.
const STALE_HOURS = 26

// How many days old a sync date is once it is past the staleness line (at
// least 1); null while it is fresh, or when nothing has been synced yet.
// Measured on when a sync last confirmed the data, never on when it last
// changed: a table nobody touched over a weekend is still fresh.
export function staleDays(date: string | null) {
	if (date === null) {
		return null
	}
	const ageHours = (Date.now() - new Date(date).getTime()) / 3_600_000
	return ageHours > STALE_HOURS ? Math.floor(ageHours / 24) || 1 : null
}

// An update is under way from the moment «Aggiorna dati ora» is pressed, not
// only once the server sees its round: the round opens when the first table is
// stored, minutes after the agent picks the request up, and a round whose
// tables were all on the server already shows up only when it closes.
export function updateInProgress(status: PoGetSystemStatusResponse['data']) {
	return (
		status.dataset.syncInProgress ||
		isRefreshInFlight(status.refresh?.status)
	)
}

// The data «Genera» reads is called «aggiornati» only when it is whole and the
// last update came through: every table at the dataset's date (none missing,
// none the server has no copy of, none left behind), and no table the agent
// failed on in the last round, nor a refresh that closed without one it
// needed. Its age is apart (staleDays). One condition for the dashboard and
// the new-document dialog, so they never tell the expert two stories.
export function datasetUpToDate(status: PoGetSystemStatusResponse['data']) {
	const dataset = status.dataset
	return (
		dataset.asOf !== null &&
		dataset.missingRequired.length === 0 &&
		dataset.unresolved.length === 0 &&
		dataset.tablesBehind.length === 0 &&
		status.refresh?.status !== 'incomplete' &&
		failedTables(status).length === 0
	)
}

// The tables the agent's last closed round failed on: they did not come with
// the last update, whatever date the dataset carries.
export function failedTables(status: PoGetSystemStatusResponse['data']) {
	return status.tables
		.filter((table) => table.lastOutcome === 'failed')
		.map((table) => table.tableName)
}

// The request waits for the agent, or its round is on the way.
export function isRefreshInFlight(
	status:
		| NonNullable<PoGetSystemStatusResponse['data']['refresh']>['status']
		| undefined
) {
	return status === 'queued' || status === 'syncing'
}
